/**
 * A button that opens a list of actions, the WAI-ARIA menu button pattern. It groups the actions
 * of a row or a page header so they stop filling the row with buttons.
 *
 * Enter, Space or the Down arrow on the button opens it with the first item focused. Up and Down
 * move between items and wrap, Home and End jump to the ends, Enter or Space chooses, Escape and Tab
 * close it, and focus goes back to the button. An item that is not available is `aria-disabled`
 * and stays reachable so it can say why, with `reason`. Typing letters jumps to the next available
 * item whose name starts with them. The letters pile up until a second passes without one, so
 * "re" finds "Remove" past "Rename". An unavailable item is passed over.
 *
 * Name an item for what it does ("Remove patient"), and mark one that ends or deletes `danger`.
 */
import * as stylex from "@stylexjs/stylex";
import type { KeyboardEvent, ReactNode } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import { focusRingInset } from "./focus.ts";
import { MoreIcon } from "./icons.tsx";
import type { Align, Side } from "./position.ts";
import { Popup } from "./Popup.tsx";
import { textStyle } from "./textStyle.ts";
const styles = {
  item: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kGNEyG: "x1cy8zhl",
    kjj79g: "xl56j7k",
    kzqmXN: "xh8yej3",
    kAzted: "xn2nem0 xlivvtk",
    k8WAf4: "x8hz100",
    kg3NbH: "xerji9v",
    kMzoRj: "xc342km",
    kMv6JI: "x1cikjda",
    kaIpWk: "x1fbwzx",
    kWkggS: "xjbqb8w x14uvmqf xhdl3qd",
    kMwMTN: "xm8xz4y",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x15406qy",
    kIyJzY: "x149xavy",
    $$css: true
  },
  danger: {
    kMwMTN: "x6ygkrl",
    $$css: true
  },
  disabled: {
    kMwMTN: "x16nvyyk",
    kkrTdU: "x1h6gzvc",
    kWkggS: "xjbqb8w",
    $$css: true
  }
};
export interface MenuItem {
  readonly label: string;
  readonly onSelect: () => void;
  readonly tone?: "danger";
  readonly disabled?: boolean;
  /** Why the item is not available, read after its name by a screen reader. */
  readonly reason?: string;
}
export interface MenuProps {
  /** Names the button and the menu: "Actions for Ada Chen". */
  readonly label: string;
  readonly items: readonly MenuItem[];
  /** The words on the button. Left out, the button is the three-dot icon alone. */
  readonly triggerContent?: string;
  /** See `Popup`: the button is named by `label`, which must contain the words on it. */
  readonly nameTrigger?: boolean;
  /** The trigger fills its container, with the words at the start and the chevron at the end. */
  readonly block?: boolean;
  readonly icon?: ReactNode;
  readonly side?: Side;
  readonly align?: Align;
  readonly testId?: string;
}
function menuItems(panel: HTMLElement): HTMLElement[] {
  return [...panel.querySelectorAll<HTMLElement>('[role="menuitem"]')];
}
function focusAt(panel: HTMLElement, pick: (items: HTMLElement[], current: number) => number) {
  const items = menuItems(panel);
  const current = items.findIndex(item => item === document.activeElement);
  items[pick(items, current)]?.focus();
}
const MOVES = {
  ArrowDown: (items: HTMLElement[], current: number) => (current + 1) % items.length,
  ArrowUp: (items: HTMLElement[], current: number) => (current <= 0 ? items.length : current) - 1,
  Home: () => 0,
  End: (items: HTMLElement[]) => items.length - 1
};
function moveFor(key: string) {
  switch (key) {
    case "ArrowDown":
    case "ArrowUp":
    case "Home":
    case "End":
      return MOVES[key];
    default:
      return undefined;
  }
}
const TYPEAHEAD_MS = 1000;

/* One buffer per open panel, so two menus never share what was typed. */
const typed = new WeakMap<HTMLElement, {
  text: string;
  at: number;
}>();
function typeahead(panel: HTMLElement, character: string) {
  const now = Date.now();
  const before = typed.get(panel);
  const text = before !== undefined && now - before.at < TYPEAHEAD_MS ? before.text + character : character;
  const items = menuItems(panel).filter(item => item.getAttribute("aria-disabled") !== "true");
  const current = items.findIndex(item => item === document.activeElement);

  /* A repeated single letter walks through the items that share it, a longer word stays on its match. */
  const start = text.length === 1 ? current + 1 : Math.max(current, 0);
  const ordered = [...items.slice(start), ...items.slice(0, start)];
  const match = ordered.find(item => (item.firstChild?.textContent ?? "").toLowerCase().startsWith(text.toLowerCase()));
  typed.set(panel, {
    text,
    at: now
  });
  match?.focus();
}
function isTyping(event: KeyboardEvent<HTMLElement>): boolean {
  return event.key.length === 1 && event.key !== " " && !event.ctrlKey && !event.metaKey && !event.altKey;
}
function onPanelKeyDown(event: KeyboardEvent<HTMLElement>, close: () => void) {
  const move = moveFor(event.key);
  if (isTyping(event)) {
    event.preventDefault();
    typeahead(event.currentTarget, event.key);
  } else if (move !== undefined) {
    event.preventDefault();
    focusAt(event.currentTarget, move);
  } else if (event.key === "Tab") {
    event.preventDefault();
    close();
  }
}
export function Menu({
  label,
  items,
  triggerContent,
  nameTrigger,
  block,
  icon,
  side,
  align,
  testId
}: MenuProps) {
  return <Popup role="menu" label={label} triggerContent={triggerContent} nameTrigger={nameTrigger} block={block} icon={icon ?? <MoreIcon />} side={side} align={align} testId={testId} onOpened={panel => focusAt(panel, () => 0)} onPanelKeyDown={onPanelKeyDown} onTriggerKeyDown={(event, open) => {
    if (event.key !== "ArrowDown") return;
    event.preventDefault();
    open();
  }}>
      {items.map((item, index) => <button key={`${String(index)}-${item.label}`} type="button" role="menuitem" tabIndex={-1} aria-disabled={item.disabled === true || undefined} onClick={event => {
      if (item.disabled === true) return;
      item.onSelect();
      event.currentTarget.closest<HTMLElement>("[popover]")?.hidePopover();
    }} {...stylex.props(textStyle.body, styles.item, item.tone === "danger" && styles.danger, item.disabled === true && styles.disabled, focusRingInset)}>
          {item.label}
          {item.disabled === true && item.reason !== undefined && <span className="xl4dl4e x1lm7cez">{item.reason}</span>}
        </button>)}
    </Popup>;
}